import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Checkbox, Tooltip } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { CalendarClock, Flame } from 'lucide-react';

import { formatDateTime, isPastDue } from '../../tasks/lib/taskMeta.js';
import type { DealStage } from '../api/deals.client.js';
import type { KanbanCardModel } from '../lib/kanban.js';
import { getSmartTagsForDeal } from './SmartTagBadge.js';
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';

const NEXT_STAGE: Partial<Record<DealStage, DealStage>> = {
  New: 'Contacted',
  Contacted: 'Negotiation',
  Negotiation: 'Won',
};

export interface DealCardProps {
  card: KanbanCardModel;
  isSelected: boolean;
  isUpdating: boolean;
  onMoveForward: (dealId: string, toStage: DealStage) => void;
  onToggleSelected: (dealId: string) => void;
  onDragStart: (dealId: string) => void;
}

/**
 * Минималистичная канбан-карточка (owner review): контакт / название / сумма
 * плюс тихие статус-иконки Flame («горячая») и CalendarClock (следующий
 * контакт, error при просрочке) с тултипами; нижние строки убраны —
 * «нужен следующий контакт» считает хедер колонки, теги живут в карточке
 * сделки. Переход стадии — hover/focus-кнопка-стрелка в правом нижнем углу.
 */
export function DealCard({
  card,
  isSelected,
  isUpdating,
  onMoveForward,
  onToggleSelected,
  onDragStart,
}: DealCardProps): ReactElement {
  useI18nSubscription();
  const location = useLocation();
  const nextStage = NEXT_STAGE[card.stage];
  // Срок следующего шага прошёл — CalendarClock краснеет (тихая семантика,
  // без заливки всей карточки).
  const overdue = isPastDue(card.nextActivityDue);
  const isHot = getSmartTagsForDeal({
    amount: card.amount,
    stage: card.stage,
    nextActivityDue: card.nextActivityDue,
  }).includes('hot');
  const nextActivityTooltip =
    card.nextActivityDue === null
      ? null
      : overdue
        ? i18n.t('crm.features.dynamic.features.deals.components.dealcard.52', {
            p0: formatDateTime(card.nextActivityDue),
          })
        : i18n.t('crm.features.dynamic.features.deals.components.dealcard.53', {
            p0: formatDateTime(card.nextActivityDue),
          });

  return (
    <article
      draggable
      data-testid={`deal-card-${card.dealId}`}
      className="crm-hover-fill crm-radius-s group relative cursor-grab text-sm transition-colors active:cursor-grabbing hover:border-[var(--ant-color-border)]"
      onDragStart={() => onDragStart(card.dealId)}
      style={{
        padding: '8px 10px',
        backgroundColor: 'var(--ant-color-bg-container)',
        border: '1px solid var(--ant-color-border-secondary)',
        boxShadow: 'none',
      }}
    >
      <Checkbox
        checked={isSelected}
        aria-label={i18n.t('crm.features.dynamic.features.deals.components.dealcard.51', {
          p0: card.title,
        })}
        onChange={() => onToggleSelected(card.dealId)}
        onClick={(event) => event.stopPropagation()}
        className="absolute"
        style={{ right: 10, top: 8 }}
      />

      {/* Контакт: 12px secondary (owner review: база карточки). */}
      <div
        className="truncate pr-6"
        style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}
      >
        {card.contactName}
      </div>

      {/* Название: 13px medium. */}
      <h4
        className="pr-6"
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--ant-color-text)',
          lineHeight: 1.3,
          margin: '1px 0 0',
          wordBreak: 'break-word',
        }}
      >
        <Link
          className="hover:underline"
          to={`/deals/${card.dealId}`}
          state={{ dealListUrl: location.pathname + location.search }}
          style={{ color: 'inherit' }}
        >
          {card.title}
        </Link>
      </h4>

      {/* Сумма mono + тихие статус-иконки (owner review: плашки → иконки). */}
      <div className="mt-1 flex items-center gap-2">
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 550,
            color: 'var(--ant-color-text)',
            ...MONO_NUMERIC_STYLE,
            letterSpacing: '-0.01em',
          }}
        >
          {card.amountLabel}
        </span>
        {isHot ? (
          <Tooltip title={i18n.t('crm.smart_tags.hot')}>
            <span
              role="img"
              aria-label={i18n.t('crm.smart_tags.hot')}
              data-testid={`deal-hot-${card.dealId}`}
              style={{ color: 'var(--ant-color-primary-border-hover, #d97706)' }}
            >
              <Flame size={14} strokeWidth={1.75} />
            </span>
          </Tooltip>
        ) : null}
        {nextActivityTooltip !== null ? (
          <Tooltip title={nextActivityTooltip}>
            <span
              role="img"
              aria-label={nextActivityTooltip}
              data-testid={`deal-next-activity-${card.dealId}`}
              style={{
                color: overdue
                  ? 'var(--ant-color-error, #b91c1c)'
                  : 'var(--ant-color-text-tertiary)',
              }}
            >
              <CalendarClock size={14} strokeWidth={1.75} />
            </span>
          </Tooltip>
        ) : null}
      </div>

      {/* Единственная тихая мета-строка: последний контакт, 11px mono tertiary. */}
      {card.lastActivityAt !== null ? (
        <div
          className="mt-1 truncate"
          style={{
            fontSize: 11,
            color: 'var(--ant-color-text-tertiary)',
            ...MONO_NUMERIC_STYLE,
          }}
        >
          {i18n.t('crm.features.features.deals.components.dealcard.последний_контакт')}{' '}
          {formatDateTime(card.lastActivityAt)}
        </div>
      ) : null}

      {/* Переход стадии: тихая кнопка-стрелка, проявляется по hover и
          focus-within (клавиатура); кликабельность сохранена. */}
      {nextStage !== undefined ? (
        <Tooltip title={getEnumLabel('dealStage', nextStage, i18n.t)}>
          <Button
            type="text"
            size="small"
            disabled={isUpdating}
            aria-label={i18n.t('crm.features.dynamic.features.deals.components.dealcard.54', {
              p0: getEnumLabel('dealStage', nextStage, i18n.t),
            })}
            onClick={(e) => {
              e.stopPropagation();
              onMoveForward(card.dealId, nextStage);
            }}
            icon={<ArrowRightOutlined />}
            className="absolute right-1 bottom-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            style={{ width: 24, height: 24, padding: 0, color: 'var(--ant-color-primary-text)' }}
          />
        </Tooltip>
      ) : null}
    </article>
  );
}
